import React from 'react'
import { Box, makeStyles, Theme } from '@material-ui/core'

import {
    WorkbookSectionsState,
    isWorkbookSectionConfigured,
    listWorkbookSections,
} from '../PipelineFlowDemo/workbookSectionDraft'
import { ApplicantDataTab } from './ApplicantDataTab'
import { FormSaveAction } from './FormSaveButton'
import { SetupArtifactShell } from './SetupArtifactShell'
import { SelectedSetupConfig, SetupConfigOption } from './setupArtifacts'
import type { PetsPolicyDraft } from './petPolicy'

const useStyles = makeStyles((theme: Theme) => ({
    wrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        height: '100%',
        minHeight: 0,
    },
    editor: {
        flex: 1,
        minHeight: 0,
    },
}))

function workbookReadiness(
    sections: WorkbookSectionsState | undefined,
): string {
    const list = listWorkbookSections(sections)
    const ready = list.filter((s) =>
        isWorkbookSectionConfigured(sections, s.bucket_id),
    ).length
    const total = list.length
    if (ready === 0) return 'No sections configured'
    if (ready === total) return 'All sections configured'
    return `${ready}/${total} sections configured`
}

export const ApplicantDataSetupTab: React.FC<{
    selected: SelectedSetupConfig | null
    onSelected: (config: SelectedSetupConfig) => void
    onClearSelection: () => void
    existing: SetupConfigOption[]
    onExistingChange: (next: SetupConfigOption[]) => void
    sections: WorkbookSectionsState | undefined
    onSectionsChange: (next: WorkbookSectionsState) => void
    contentLocked?: boolean
    artifactEditable?: boolean
    saveAction?: FormSaveAction
    petsPolicy?: PetsPolicyDraft
}> = ({
    selected,
    onSelected,
    onClearSelection,
    existing,
    onExistingChange,
    sections,
    onSectionsChange,
    contentLocked = false,
    artifactEditable = true,
    saveAction,
    petsPolicy,
}) => {
    const classes = useStyles()
    const readOnly =
        !!selected?.orgMandated || contentLocked || !artifactEditable
    return (
        <SetupArtifactShell
            kind="applicant_data"
            selected={selected}
            onSelected={onSelected}
            onClearSelection={onClearSelection}
            existing={existing}
            onExistingChange={onExistingChange}
            readinessLabel={selected ? workbookReadiness(sections) : undefined}
            contentLocked={contentLocked}
            saveAction={readOnly ? undefined : saveAction}
        >
            <Box className={classes.wrap}>
                <Box className={classes.editor}>
                    <ApplicantDataTab
                        sections={sections}
                        onSectionsChange={onSectionsChange}
                        readOnly={readOnly}
                        petsPolicy={petsPolicy}
                    />
                </Box>
            </Box>
        </SetupArtifactShell>
    )
}
